import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_ORIGIN } from '@/constants';
import { isPublicSharePath } from '@/constants/public-share-routes';
import { isYeshumanConceptsPath } from '@/constants/yeshuman-marketing-routes';
import { isJwtExpired } from '@/utils/jwt';
import { getAuthAccessToken, initializeAuthorizedFetch, setAuthAccessToken } from '@/lib/api';
import { redirectToLogin } from '@/lib/auth-return';
import { clearUserSessionCache } from '@/lib/user-session-cache';

// Marketing, auth, and shareable public artifact routes — no login redirect.
const isOnPublicAuthPage = () => {
  const path = window.location.pathname;
  if (isPublicSharePath(path) || isYeshumanConceptsPath(path)) return true;
  return (
    path === '/' ||
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/auth/reset-password') ||
    path.startsWith('/auth/verify-email') ||
    path.startsWith('/auth/invite') ||
    path.startsWith('/auth/provider') ||
    path.startsWith('/apply')
  );
};

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  /** False when the account has no password yet (e.g. magic-link signup). Omitted when unknown. */
  has_usable_password?: boolean;
}

interface RegisterResponse {
  success: boolean;
  error?: string;
  email_verification_required?: boolean;
  approval_pending?: boolean;
  access_token?: string;
  user?: User;
  onboarding_required?: boolean;
  onboarding_redirect?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<{
    success: boolean
    error?: string
    approval_pending?: boolean
    approval_rejected?: boolean
  }>;
  register: (email: string, password: string, options?: { signupRole?: string; captchaToken?: string; jobInviteToken?: string }) => Promise<RegisterResponse>;
  logout: () => void;
  loginWithTokens: (token: string, user: User) => void;
  isAuthenticated: boolean;
  refreshThreads: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Same-origin: API_ORIGIN is '' → /api/... ; cross-origin: full origin + /api/...

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const logout = useCallback(() => {
    setAuthAccessToken(null);
    setUser(null);
    setToken(null);
    clearUserSessionCache(queryClient);
  }, [queryClient]);

  // Load auth state from localStorage on mount
  useEffect(() => {
    console.log('useAuth: Loading auth state from localStorage');
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      try {
        // Check if token is expired before setting it
        if (isJwtExpired(storedToken)) {
          console.log('useAuth: Stored token is expired, clearing auth state and redirecting to login');
          setAuthAccessToken(null);
          setIsInitialized(true);
          if (!isOnPublicAuthPage()) {
            redirectToLogin()
          }
        } else {
          console.log('useAuth: Setting token and user from localStorage');
          setAuthAccessToken(storedToken);
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('useAuth: Failed to parse stored auth data:', error);
        setAuthAccessToken(null);
        setIsInitialized(true);
        if (!isOnPublicAuthPage()) {
          redirectToLogin()
        }
      }
    } else {
      console.log('useAuth: No stored auth data found');
      // Only redirect to login if we're not on a public auth page
      // In development, skip redirect during hot reload to prevent auth loss
      const isDev = import.meta.env.DEV;
      if (!isOnPublicAuthPage() && !isDev) {
        // Only redirect in production - in dev, let the app render and handle auth via API calls
        setIsInitialized(true);
        redirectToLogin()
      } else if (!isOnPublicAuthPage() && isDev) {
        // In dev mode, check again after a brief delay (hot reload might clear state temporarily)
        setTimeout(() => {
          const retryToken = localStorage.getItem('auth_token');
          const retryUser = localStorage.getItem('auth_user');
          if (!retryToken || !retryUser) {
            // Only redirect if still no auth after delay
            console.log('useAuth: Still no auth after delay, redirecting to login');
            setIsInitialized(true);
            redirectToLogin()
          } else {
            console.log('useAuth: Found auth data after delay, restoring state');
            setAuthAccessToken(retryToken);
            setToken(retryToken);
            setUser(JSON.parse(retryUser));
            setIsInitialized(true);
          }
        }, 200);
      } else {
        // On public auth page with no stored data
        setIsInitialized(true);
      }
    }
  }, []);

  // Refresh profile flags (e.g. has_usable_password) from the server when we have a valid token.
  useEffect(() => {
    if (!token || isJwtExpired(token)) return;
    let cancelled = false;
    const requestToken = token;
    void (async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/accounts/me`, {
          headers: { Authorization: `Bearer ${requestToken}` },
        });
        if (cancelled) return;
        if (response.status === 401) {
          // Ignore stale /accounts/me responses that no longer match the active session.
          if (getAuthAccessToken() !== requestToken) return;
          logout();
          return;
        }
        if (!response.ok) return;
        const me = await response.json() as User & { has_usable_password?: boolean };
        const next: User = {
          id: me.id,
          username: me.username,
          email: me.email,
          first_name: me.first_name,
          last_name: me.last_name,
          has_usable_password: me.has_usable_password,
        };
        setUser(next);
        localStorage.setItem('auth_user', JSON.stringify(next));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  // Check token expiry when user returns to tab (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken && isJwtExpired(storedToken)) {
          console.log('useAuth: Token expired on tab focus, redirecting to login');
          logout();
          if (!isOnPublicAuthPage()) {
            redirectToLogin()
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [logout]);

  // Check token expiry periodically
  useEffect(() => {
    const checkTokenExpiry = () => {
      if (token && isJwtExpired(token)) {
        console.log('Token expired during session, clearing auth state and redirecting to login');
        logout();
        if (!isOnPublicAuthPage()) {
          redirectToLogin()
        }
      }
    };

    // Check every 60 seconds
    const interval = setInterval(checkTokenExpiry, 60000);

    return () => clearInterval(interval);
  }, [token, logout]);

  // Initialize authorizedFetch with auth functions
  useEffect(() => {
    // Create a no-op promptLogin since we removed that functionality
    const promptLogin = () => {
      if (!isOnPublicAuthPage()) {
        redirectToLogin()
      }
    };
    initializeAuthorizedFetch(logout, promptLogin);
  }, [logout]);

  const login = async (
    email: string,
    password: string,
  ): Promise<{
    success: boolean
    error?: string
    approval_pending?: boolean
    approval_rejected?: boolean
  }> => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_ORIGIN}/api/accounts/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Use access_token from backend (allauth JWT strategy)
        const tokenToUse = data.access_token || data.token;
        
        if (!tokenToUse) {
          return { success: false, error: 'No authentication token received' };
        }

        setAuthAccessToken(tokenToUse);
        setUser(data.user);
        setToken(tokenToUse);
        localStorage.setItem('auth_user', JSON.stringify(data.user));

        return { success: true };
      } else {
        return {
          success: false,
          error: data.error || 'Login failed',
          approval_pending: Boolean(data.approval_pending),
          approval_rejected: Boolean(data.approval_rejected),
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    options?: { signupRole?: string; captchaToken?: string; jobInviteToken?: string },
  ): Promise<RegisterResponse> => {
    setIsLoading(true);

    try {
      const body: Record<string, string> = {
        email,
        password,
      };
      if (options?.signupRole) {
        body.signup_role = options.signupRole;
      }
      if (options?.captchaToken) {
        body.captcha_token = options.captchaToken;
      }
      if (options?.jobInviteToken) {
        body.job_invite_token = options.jobInviteToken;
      }

      const response = await fetch(`${API_ORIGIN}/api/accounts/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const onboarding_required = Boolean(data.onboarding_required);
        const onboarding_redirect = Boolean(data.onboarding_redirect);

        // If email verification is required, don't log in yet
        if (data.email_verification_required) {
          return {
            success: true,
            email_verification_required: true,
            approval_pending: Boolean(data.approval_pending),
            onboarding_required,
            onboarding_redirect,
          };
        }

        if (data.approval_pending) {
          return {
            success: true,
            approval_pending: true,
            onboarding_required,
            onboarding_redirect: false,
          };
        }

        // If tokens are provided, log the user in automatically
        const tokenToUse = data.access_token || data.token;
        if (tokenToUse && data.user) {
          setAuthAccessToken(tokenToUse);
          setUser(data.user);
          setToken(tokenToUse);
          localStorage.setItem('auth_user', JSON.stringify(data.user));

          return {
            success: true,
            email_verification_required: false,
            access_token: tokenToUse,
            user: data.user,
            onboarding_required,
            onboarding_redirect,
          };
        }

        // Success but no tokens (shouldn't happen, but handle gracefully)
        return {
          success: true,
          email_verification_required: false,
          onboarding_required,
          onboarding_redirect,
        };
      } else {
        // Handle validation errors from backend
        let errorMessage = 'Failed to create account';
        if (data.error) {
          errorMessage = data.error;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.errors && typeof data.errors === 'object') {
          const errorKeys = Object.keys(data.errors);
          if (errorKeys.length > 0) {
            const firstError = data.errors[errorKeys[0]];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        }

        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTokens = (accessToken: string, nextUser: User) => {
    setAuthAccessToken(accessToken);
    setUser(nextUser);
    setToken(accessToken);
    localStorage.setItem('auth_user', JSON.stringify(nextUser));
  };

  const refreshThreads = () => {
    // This function can be implemented to refresh threads
    // For now, it's a placeholder to satisfy the interface
    console.log('Refresh threads called');
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isInitialized,
    login,
    register,
    logout,
    loginWithTokens,
    isAuthenticated: !!user && !!token,
    refreshThreads,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
